<template>
  <BaseCard>
    <div class="px-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div class="text-lg leading-none font-semibold break-words">{{ kpiName }}</div>

      <!-- Modal: gráfico ampliado -->
      <BaseDialog
        :open.sync="isChartOpen"
        :contentClass="'overflow-y-auto'"
        :contentStyle="{ maxHeight: '90vh', width: '95vw', maxWidth: '95vw' }"
      >
        <template #header>
          <div class="flex flex-col gap-2">
            <div class="text-lg leading-none font-semibold">{{ kpiName }}</div>
            <div class="text-xs text-muted-foreground">Visualização ampliada</div>
          </div>
        </template>

        <template #default>
          <div :style="{ height: '70vh' }" class="w-full">
            <!-- v-if para garantir render correto quando o modal abre -->
            <KPIBarChart
              v-if="isChartOpen"
              :chartData="barChartData"
              :format="format"
              :lowerIsBetter="isLowerIsBetter"
              :showMeta="showMeta"
              :realizadoLabel="realizadoLabel"
            />
          </div>
        </template>
      </BaseDialog>

      <BaseDialog
        v-if="!readonly && !isDerivedKpi"
        :open.sync="isOpen"
        :contentClass="'!max-w-5xl overflow-y-auto'"
        :contentStyle="{ maxHeight: '80vh', minWidth: 'min(90vw, 56rem)' }"
      >
        <template #header>
          <div class="flex flex-col gap-2 text-center sm:text-left">
            <div class="text-lg leading-none font-semibold">
              {{ metaTypeInModal === 'semanal' ? 'Inserir Resultados Semanais' : 'Inserir Resultados Mensais' }} - {{ kpiName }}
            </div>
            <div v-if="showMeta" class="flex flex-wrap items-center gap-2 mt-2">
              <span class="text-sm font-medium text-muted-foreground">Tipo de meta:</span>
              <BaseButton
                size="sm"
                :variant="metaTypeInModal === 'mensal' ? 'default' : 'outline'"
                @click="metaTypeInModal = 'mensal'"
              >
                Mensal
              </BaseButton>
              <BaseButton
                v-if="allowMensalAndWeekly"
                size="sm"
                :variant="metaTypeInModal === 'semanal' ? 'default' : 'outline'"
                @click="metaTypeInModal = 'semanal'"
              >
                Semanal
              </BaseButton>
            </div>
          </div>
        </template>

        <template #default>
          <div v-if="metaTypeInModal === 'mensal'" class="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
            <div v-for="(month, idx) in months" :key="month" class="space-y-2 min-w-[11rem]">
              <BaseLabel :forId="`input-${sector}-${kpiKey}-${month}`" className="text-sm font-medium">
                {{ month }}
              </BaseLabel>
              <div v-if="showMeta" class="text-xs text-muted-foreground mb-1">Meta: {{ formatValue(metas[idx], format) }}</div>
              <div v-else-if="showYtdInModal" class="text-xs text-muted-foreground mb-1">
                Acumulado no ano: {{ formatValue(ytdForMonthIndex(idx), format) }}
              </div>
              <BaseInput
                :value="monthlyInputValue(month)"
                :placeholder="placeholderFor(month)"
                @input="(v) => handleInputChange(month, v)"
                :className="'min-w-[10rem]'"
              />
            </div>
          </div>

          <div v-else class="mt-4 space-y-6 overflow-x-auto">
            <div
              v-for="(month, monthIdx) in months"
              :key="month"
              class="border rounded-lg p-4 bg-muted/30 min-w-0"
            >
              <div class="text-sm font-semibold mb-3">{{ month }}</div>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4" style="grid-template-columns: repeat(4, minmax(11rem, 1fr));">
                <div v-for="w in 4" :key="w" class="space-y-2 min-w-[11rem]">
                  <BaseLabel :forId="`input-w-${sector}-${kpiKey}-${month}-${w}`" className="text-sm font-medium">
                    Semana {{ w }}
                  </BaseLabel>
                  <div v-if="showMeta" class="text-xs text-muted-foreground mb-1">
                    Meta: {{ formatValue(metaForWeek(month, w), format) }}
                  </div>
                  <BaseInput
                    :id="`input-w-${sector}-${kpiKey}-${month}-${w}`"
                    :value="weeklyInputValue(month, w)"
                    :placeholder="placeholderForWeek(month, w)"
                    @input="(v) => handleWeeklyInputChange(month, w, v)"
                    :className="'min-w-[10rem]'"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="flex justify-end gap-2 mt-6">
            <BaseButton variant="outline" @click="isOpen = false">Cancelar</BaseButton>
            <BaseButton @click="handleSaveAll">Salvar Todos</BaseButton>
          </div>
        </template>
      </BaseDialog>

      <div class="flex flex-row items-center gap-3 shrink-0">
        <BaseButton
          v-if="allowMensalAndWeekly"
          size="sm"
          className="!min-h-0 !h-7 !px-2.5 !text-xs"
          :variant="chartViewMode === 'mensal' ? 'default' : 'outline'"
          @click="chartViewMode = 'mensal'"
        >
          Mensal
        </BaseButton>
        <BaseButton
          size="sm"
          className="!min-h-0 !h-7 !px-2.5 !text-xs"
          :variant="chartViewMode === 'anual' ? 'default' : 'outline'"
          @click="chartViewMode = 'anual'"
        >
          Anual
        </BaseButton>
        <BaseButton
          v-if="!readonly && !isDerivedKpi"
          size="sm"
          variant="outline"
          className="hover:bg-primary hover:text-primary-foreground"
          @click="isOpen = true"
        >
          <IconPlusCircle :className="'w-4 h-4 mr-2'" />
          Inserir Resultados
        </BaseButton>
      </div>
    </div>

    <div v-if="chartViewMode === 'mensal'" class="px-6 grid grid-cols-6 gap-2 mt-2">
      <BaseButton
        v-for="(month, idx) in months"
        :key="month"
        size="sm"
        :variant="selectedMonthIndex === idx ? 'default' : 'outline'"
        @click="selectedMonthIndex = idx"
      >
        {{ month }}
      </BaseButton>
    </div>

    <div class="px-6">
      <div v-if="isLoading" class="flex items-center justify-center h-72 sm:h-80 md:h-96">
        <div class="text-muted-foreground">Carregando dados...</div>
      </div>
      <!-- altura precisa comportar também a legenda do Chart.js -->
      <div v-else class="relative w-full h-72 sm:h-80 md:h-96 cursor-zoom-in" @click="isChartOpen = true">
        <KPIBarChart
          class="mt-4"
          :chartData="barChartData"
          :format="format"
          :lowerIsBetter="isLowerIsBetter"
          :showMeta="showMeta"
          :realizadoLabel="realizadoLabel"
        />
      </div>
    </div>
  </BaseCard>
</template>

<script>
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import BaseDialog from '@/components/ui/BaseDialog.vue'
import BaseInput from '@/components/ui/BaseInput.vue'
import BaseLabel from '@/components/ui/BaseLabel.vue'
import IconPlusCircle from '@/components/icons/IconPlusCircle.vue'

import KPIBarChart from '@/components/charts/KPIBarChart'
import { months, formatValue } from '@/lib/kpi-data'

export default {
  name: 'KPIChartCard',
  components: { BaseCard, BaseButton, BaseDialog, BaseInput, BaseLabel, KPIBarChart, IconPlusCircle },
  props: {
    sector: { type: String, required: true },
    kpiKey: { type: String, required: true },
    kpiName: { type: String, required: true },
    metas: { type: Array, required: true },
    format: { type: String, required: true },
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      isOpen: false,
      isChartOpen: false,
      monthlyValues: {},
      weeklyValues: {}, // { 'Jan-1': value, 'Jan-2': value, ... }
      months,
      metaTypeInModal: 'mensal', // 'mensal' | 'semanal'
      chartViewMode: 'anual', // 'anual' | 'mensal'
      selectedMonthIndex: 0, // para modo mensal: 0 = Jan, 1 = Fev, ...
    }
  },
  computed: {
    isLoading() {
      return this.$store.state.kpis.isLoading
    },
    isDerivedKpi() {
      // KPIs derivados: sem botão "Inserir Resultados"
      if (this.sector === 'geral') {
        const k = (this.kpiKey || '').toLowerCase().replace(/\s/g, '_')
        if (k === 'faturamento') return true
      }
      if (this.sector === 'comercial') {
        const k = (this.kpiKey || '').toLowerCase().replace(/\s/g, '_')
        if (k === 'meta_lideres' || k.includes('meta_lideres')) return true
        if (k === 'meta_b2b' || k === '3_-_meta_b2b') return true
        if (k === 'meta_geral' || k.includes('meta_geral')) return true
      }
      return false
    },
    // Vista mensal e aba semanal só para comercial, marketing e ti (demais setores: ocultar)
    allowMensalAndWeekly() {
      return ['comercial', 'marketing', 'ti'].includes(this.sector)
    },
    showMeta() {
      if (this.sector !== 'compras') return true
      return ![
        'aquisicoes',
        'qtd_reservas_diarias_salas',
        'qtd_arquisicao_coffee',
        'qtd_reservas_hospedagem',
      ].includes(this.kpiKey)
    },
    showYtdInModal() {
      return this.sector === 'compras' && this.kpiKey === 'aquisicoes'
    },
    realizadoLabel() {
      if (this.sector === 'compras' && this.kpiKey === 'aquisicoes') return 'Quantidade de produtos adquiridos'
      return 'Realizado'
    },
    /** Marketing (aba): marketing, cac, cpl e investimento = cores invertidas quando >= meta (vermelho) */
    isLowerIsBetter() {
      if (this.sector === 'marketing') {
        return ['cac', 'cpl', 'investimento'].includes(this.kpiKey)
      }
      if (this.sector === 'sucesso' && this.kpiKey === 'espera') return true
      return false
    },
    kpiMonthMap() {
      // Acesso direto ao state para garantir reatividade (getters que retornam função não reagem bem em computed).
      return this.$store.state.kpis.data?.[this.sector]?.[this.kpiKey] || {}
    },
    selectedMonth() {
      return months[this.selectedMonthIndex] || months[0]
    },
    dataWeeklyForSector() {
      return this.$store.state.kpis.dataWeekly?.[this.sector]?.[this.kpiKey] || {}
    },
    barChartData() {
      if (this.chartViewMode === 'anual') {
        const labels = months
        const meta = []
        const realizado = []
        labels.forEach((m, idx) => {
          const d = this.kpiMonthMap?.[m]
          meta.push(this.showMeta ? Number(this.metas[idx] ?? d?.meta ?? 0) : 0)
          realizado.push(Number(d?.realizado ?? 0))
        })
        return { labels, meta, realizado }
      }
      // Mensal: 4 semanas do mês selecionado (todos os KPIs do banco, inclusive Meta Geral)
      const month = this.selectedMonth
      const labels = ['Semana 1', 'Semana 2', 'Semana 3', 'Semana 4']
      const meta = this.showMeta ? [1, 2, 3, 4].map((w) => this.metaForWeek(month, w)) : [0, 0, 0, 0]
      const realizado = [1, 2, 3, 4].map((w) => this.realizedForWeek(month, w))
      return { labels, meta, realizado }
    },
  },
  watch: {
    isOpen(open) {
      if (!open) {
        this.monthlyValues = {}
        this.weeklyValues = {}
      } else if (!this.allowMensalAndWeekly) {
        this.metaTypeInModal = 'mensal'
      } else if (!this.showMeta) {
        this.metaTypeInModal = 'mensal'
      }
    },
    allowMensalAndWeekly(allow) {
      if (!allow) {
        if (this.chartViewMode === 'mensal') this.chartViewMode = 'anual'
        if (this.metaTypeInModal === 'semanal') this.metaTypeInModal = 'mensal'
      }
    },
    showMeta(show) {
      if (!show) {
        if (this.chartViewMode === 'mensal') this.chartViewMode = 'anual'
        if (this.metaTypeInModal === 'semanal') this.metaTypeInModal = 'mensal'
      }
    },
  },
  methods: {
    formatValue,
    maskCurrencyInput(raw) {
      const s = String(raw ?? '')
      // mantém apenas dígitos; interpreta como centavos (máscara comum de currency)
      const digits = s.replace(/\D/g, '')
      if (!digits) return ''
      const value = Number(digits) / 100
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
    },
    parseCurrencyInput(masked) {
      const s = String(masked ?? '').trim()
      if (!s) return null
      // "1.234.567,89" -> "1234567.89"
      const normalized = s.replace(/\./g, '').replace(',', '.')
      const n = Number.parseFloat(normalized)
      return Number.isNaN(n) ? null : n
    },
    realizedForMonthFromWeeks(month) {
      let sum = 0
      for (let w = 1; w <= 4; w++) {
        sum += this.realizedForWeek(month, w)
      }
      return sum
    },
    placeholderFor(month) {
      const currentValue = this.realizedForMonthFromWeeks(month)
      return currentValue > 0 ? formatValue(currentValue, this.format) : '0'
    },
    monthlyInputValue(month) {
      if (this.monthlyValues[month] !== undefined && this.monthlyValues[month] !== null && String(this.monthlyValues[month]).trim() !== '') {
        return this.monthlyValues[month]
      }
      const value = this.realizedForMonthFromWeeks(month)
      return value > 0 ? formatValue(value, this.format) : ''
    },
    metaForWeek(month, weekNum) {
      const d = this.$store.getters['kpis/getWeeklyData'](this.sector, this.kpiKey, month, weekNum)
      return d != null ? Number(d.meta ?? 0) : 0
    },
    realizedForWeek(month, weekNum) {
      const d = this.$store.getters['kpis/getWeeklyData'](this.sector, this.kpiKey, month, weekNum)
      return d != null ? Number(d.realizado ?? 0) : 0
    },
    realizedForWeekFormatted(month, weekNum) {
      const value = this.realizedForWeek(month, weekNum)
      return value > 0 ? formatValue(value, this.format) : ''
    },
    weeklyInputValue(month, weekNum) {
      const key = `${month}-${weekNum}`
      if (this.weeklyValues[key] !== undefined && this.weeklyValues[key] !== null && this.weeklyValues[key] !== '') {
        return this.weeklyValues[key]
      }
      return this.realizedForWeekFormatted(month, weekNum)
    },
    placeholderForWeek(month, weekNum) {
      const value = this.realizedForWeek(month, weekNum)
      return value > 0 ? formatValue(value, this.format) : '0'
    },
    handleWeeklyInputChange(month, weekNum, value) {
      if (this.format === 'currency') {
        this.$set(this.weeklyValues, `${month}-${weekNum}`, this.maskCurrencyInput(value))
        return
      }
      this.$set(this.weeklyValues, `${month}-${weekNum}`, value)
    },
    handleInputChange(month, value) {
      if (this.format === 'currency') {
        this.$set(this.monthlyValues, month, this.maskCurrencyInput(value))
        return
      }
      this.$set(this.monthlyValues, month, value)
    },
    parsedMonthlyEditedValue(month) {
      const raw = this.monthlyValues?.[month]
      if (raw === undefined || raw === null || String(raw).trim() === '') {
        return this.realizedForMonthFromWeeks(month)
      }
      if (this.format === 'currency') {
        const n = this.parseCurrencyInput(raw)
        return n != null && !Number.isNaN(n) ? Number(n) : this.realizedForMonthFromWeeks(month)
      }
      const n = Number.parseFloat(String(raw).replace(',', '.'))
      return Number.isNaN(n) ? this.realizedForMonthFromWeeks(month) : Number(n)
    },
    ytdForMonthIndex(idx) {
      let sum = 0
      for (let i = 0; i <= idx && i < months.length; i++) {
        const m = months[i]
        sum += this.parsedMonthlyEditedValue(m)
      }
      return sum
    },
    async handleSaveAll() {
      const isMensal = this.metaTypeInModal === 'mensal'
      if (isMensal) {
        const items = []
        for (const month of months) {
          const value = this.monthlyValues[month]
          if (value && String(value).trim() !== '') {
            const numericValue =
              this.format === 'currency'
                ? this.parseCurrencyInput(value)
                : Number.parseFloat(String(value).replace(',', '.'))
            if (numericValue != null && !Number.isNaN(numericValue)) {
              items.push({ month, value: numericValue })
            }
          }
        }
        await this.$store.dispatch('kpis/updateRealizadoBulk', {
          sector: this.sector,
          kpiKey: this.kpiKey,
          items,
        })
        this.monthlyValues = {}
      } else {
        const items = []
        for (const month of months) {
          for (let w = 1; w <= 4; w++) {
            const key = `${month}-${w}`
            const raw = this.weeklyValues[key]
            const value =
              raw !== undefined && raw !== null && String(raw).trim() !== ''
                ? this.format === 'currency'
                  ? this.parseCurrencyInput(raw)
                  : Number.parseFloat(String(raw).replace(',', '.'))
                : this.realizedForWeek(month, w)
            if (value != null && !Number.isNaN(value)) {
              items.push({ month, week: w, value: Number(value) })
            }
          }
        }
        if (items.length > 0) {
          await this.$store.dispatch('kpis/updateRealizadoBulk', {
            sector: this.sector,
            kpiKey: this.kpiKey,
            items,
          })
        }
        this.weeklyValues = {}
      }
      // Refetch do setor atual para todos os gráficos/cards do setor exibirem dados atualizados
      await this.$store.dispatch('kpis/fetchSector', { sector: this.sector })
      this.isOpen = false
    },
  },
}
</script>

